// frontend/src/pages/builder/ChartBuilderPage.jsx
// The new chart builder (Phase 3), built at /charts/build next to the old
// one until cutover. 6a: pick a dataset, see a live preview. 6b: pick
// fields from the dataset's field list; the preview follows. 6c-1: the
// wells show what is in the chart, each chip removable. 6c-2: drag fields
// onto a well; change a value's aggregation or a date's grain on its chip.
// 7a: pick the chart type; types the fields can't draw are greyed out with
// the reason. 7b-1b: data settings (sort, row limit, empty periods).
// 7c-2: the graphite look (design decision D9): three panels, Data | chart |
// Visual, with everything about the visual (type, slots, settings) on the right.
// 7b-2c: Format -- the chart's title (automatic until typed) and how its
// numbers are written (number format, currency, decimals, value labels).
// 7d-2 (decision D11): the Table type has one Columns slot instead of the
// three wells: up to 20 grouping columns and 10 values in the user's order
// (kept beside the draft as a list of keys), with click-to-sort headings.
// 7d-3: either side panel collapses to a rail so the chart gets the room, the
// preview fills the window's height, and a table's columns can be dragged
// wider or narrower (the widths travel in presentation.columns).
// 7e-3 (decision D12): the Matrix type has Rows / Columns / Values slots and a
// Totals switch; the layout ({ rows, columns, totals }) is worked out from the
// draft and sent as spec.matrix, and the preview draws it with MatrixView.
// Step 8: filters, in the Data panel under the fields; only complete ones go
// into the spec (filters.js), and the chart's subtitle names the applied ones.
// Step 9: Save (admins and analysts) stores the chart through /charts and
// keeps you here at ?chart=ID; opening that URL restores every slot, setting,
// filter and format (savedChart.js), so a chart is edited where it was made.
// Step 10: "Unsaved changes" shows while the chart differs from what was last
// saved or opened, and the browser asks before leaving with unsaved work.
// Step 10c: "Save as copy" saves what is on screen as a new private chart in
// the original's folder and keeps editing the copy; the original is untouched
// (another team's variant: open, change the filter, save as copy).
// Step 13c-3: the field list also offers the dataset's calculated columns (as
// fields) and its saved measures (role "metric", values only; builderSpec.js).
// Only the caller's own datasets and fields are listed (both endpoints are
// tenant-scoped server-side); the preview runs through POST /semantic/render.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, Database, Loader2, PanelLeftClose, PanelRightClose, Table2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { EmptyState } from '@/components/ui/empty-state.jsx';
import { Panel, PanelDivider, PanelHint, PanelLabel, PanelTitle } from '@/components/ui/panel.jsx';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select.jsx';
import { createChart, getChart, getFields, listDatasets, updateChart } from '../../charts/semanticApi';
import { hasAnyRole } from '../../utils/roles';
import { copyTitle, fromSavedChart, isBuilderChart, saveFailureMessage, toSavePayload } from './savedChart';
import ChartView from '../../charts/ChartView';
import FieldList from './FieldList';
import Wells from './Wells';
import ChartTypeSwitcher from './ChartTypeSwitcher';
import { availability, resolveType } from './chartTypes';
import DataSettingsPanel from './DataSettingsPanel';
import { applySettings, effectiveSort, emptySettings, toggleSort } from './dataSettings';
import FormatPanel from './FormatPanel';
import { chartTitle, emptyFormat, toPresentation } from './formatSettings';
import ColumnsWell from './ColumnsWell';
import MatrixWells from './MatrixWells';
import FiltersPanel from './FiltersPanel';
import { addFilter, filterProblem, filterSummary, specFilters, withFilters } from './filters';
import {
  addToMatrix, dropOnMatrix, emptyMatrix, matrixLayout, matrixSpec, moveMatrixChip, removeMatrixItem,
} from './matrixSlots';
import {
  addTableColumn, alignDraft, columnsPresentation, insertKey, moveKey, orderedChips, replaceKey, setColumnUse,
} from './tableColumns';
import { targetKey } from '../../charts/tableOrder';
import { gridClass, readPanes, writePanes } from './layout';
import {
  addField, dropField, emptyDraft, fieldsFromResponse, measureLabel, moveChip, removeItem, setAggregation, setGrain, toSpec,
} from './builderSpec';

function sourceLabel(key) {
  const k = String(key || 'other').replace(/_/g, ' ');
  return k.charAt(0).toUpperCase() + k.slice(1);
}

/** Datasets grouped by source, sources and datasets in name order. */
export function groupDatasets(datasets) {
  const groups = new Map();
  for (const d of datasets) {
    const key = d.source_key || 'other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(d);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => ({
      key,
      label: sourceLabel(key),
      items: [...items].sort((a, b) => String(a.display_name).localeCompare(String(b.display_name))),
    }));
}

/** "Count of records by Department and Level": what the chart shows, until a title is typed (7b-2c). */
export function describeDraft(draft, fieldsByKey = {}) {
  const name = (key) => (fieldsByKey[key] && fieldsByKey[key].display_name) || key;
  const values = draft.measures.length ? draft.measures.map((m) => measureLabel(m, fieldsByKey)).join(', ') : 'Count of records';
  const dims = draft.dimensions.map((d) => name(d.field));
  if (!dims.length) return values;
  return `${values} by ${dims.length > 1 ? `${dims.slice(0, -1).join(', ')} and ${dims[dims.length - 1]}` : dims[0]}`;
}

const MIN_PREVIEW = 380;

/**
 * The height of a box that the panel's flex layout sizes, for the chart
 * inside it. Returns [height, ref]: the ref is a callback, so the observer
 * attaches whenever the box mounts (the page shows a spinner before it does).
 */
function useBoxHeight() {
  const [h, setH] = useState(MIN_PREVIEW);
  const [el, setEl] = useState(null);
  useLayoutEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const read = () => setH(Math.max(MIN_PREVIEW, Math.floor(el.getBoundingClientRect().height)));
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [h, setEl];
}

/** A collapsed side panel: one button, drawn as what the panel holds (a table of data, a chart), that opens it again. */
function Rail({ icon: Icon, label, onOpen }) {
  return (
    <Panel className="flex justify-center p-1.5">
      <button type="button" onClick={onOpen} aria-label={`Show the ${label} panel`} title={`Show ${label}`}
        className="rounded-lg p-1.5 text-[var(--ui-text-muted)] hover:bg-[var(--ui-inset-hover)] hover:text-[var(--ui-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
    </Panel>
  );
}

function PanelHeader({ title, side, onCollapse }) {
  const Icon = side === 'left' ? PanelLeftClose : PanelRightClose;
  return (
    <div className="mb-3 flex items-center justify-between">
      <PanelTitle className="mb-0">{title}</PanelTitle>
      <button type="button" onClick={onCollapse} aria-label={`Hide the ${title} panel`} title="Hide"
        className="-mr-1 rounded-lg p-1 text-[var(--ui-text-muted)] hover:bg-[var(--ui-inset-hover)] hover:text-[var(--ui-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export default function ChartBuilderPage() {
  const navigate = useNavigate();
  const [panes, setPanes] = useState(readPanes);
  const togglePane = (which) => setPanes((p) => { const next = { ...p, [which]: !p[which] }; writePanes(next); return next; });
  const [previewHeight, chartBox] = useBoxHeight();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState({ status: 'loading', datasets: [], error: null });
  const [attempt, setAttempt] = useState(0);
  const canSave = hasAnyRole('admin', 'analyst');

  // --- a saved chart to edit (step 9): ?chart=ID -------------------------------
  // The chart is fetched, its dataset is chosen (which resets the builder),
  // and once that dataset's fields have loaded the chart's state is restored.
  const chartParam = Number(searchParams.get('chart')) || null;
  const [saved, setSaved] = useState({ status: 'idle', chart: null, error: null });   // the chart being edited
  const [saving, setSaving] = useState({ status: 'idle', error: null });
  const pendingRestore = useRef(null);
  const [baseline, setBaseline] = useState(null);   // the chart as last saved or opened, as specKey
  const awaitBaseline = useRef(false);
  useEffect(() => {
    if (!chartParam) { setSaved({ status: 'idle', chart: null, error: null }); return undefined; }
    const ctrl = new AbortController();
    setSaved({ status: 'loading', chart: null, error: null });
    getChart(chartParam, { signal: ctrl.signal })
      .then((chart) => {
        if (ctrl.signal.aborted) return;
        setSaved({ status: 'ready', chart, error: null });
        if (isBuilderChart(chart)) {
          pendingRestore.current = chart;
          const next = new URLSearchParams(window.location.search);
          next.set('dataset', String(chart.dataset_id));
          next.set('chart', String(chart.id));
          setSearchParams(next, { replace: true });
        }
      })
      .catch((error) => { if (!ctrl.signal.aborted) setSaved({ status: 'error', chart: null, error }); });
    return () => ctrl.abort();
  }, [chartParam]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ctrl = new AbortController();
    setState((s) => ({ ...s, status: 'loading', error: null }));
    listDatasets({ signal: ctrl.signal })
      .then((datasets) => setState({ status: 'ready', datasets, error: null }))
      .catch((error) => { if (!ctrl.signal.aborted) setState({ status: 'error', datasets: [], error }); });
    return () => ctrl.abort();
  }, [attempt]);

  const groups = useMemo(() => groupDatasets(state.datasets), [state.datasets]);
  // The selection lives in the URL (?dataset=12) so a refresh keeps it; an id
  // that is not in the caller's own list is ignored.
  const selectedId = Number(searchParams.get('dataset')) || null;
  const selected = state.datasets.find((d) => d.id === selectedId) || null;

  // Fields of the selected dataset; the draft (chosen fields) starts empty
  // for every dataset, since fields never carry over between datasets.
  const [fieldState, setFieldState] = useState({ status: 'idle', fields: [], error: null });
  const [draft, setDraft] = useState(emptyDraft);
  const [notice, setNotice] = useState(null);
  const [chosenType, setChosenType] = useState(null);   // null = use the suggested type
  const [settings, setSettings] = useState(emptySettings);
  const [format, setFormat] = useState(emptyFormat);
  const [order, setOrder] = useState([]);       // the table's column order, as keys (7d-2)
  const [widths, setWidths] = useState({});     // dragged column widths by key (7d-3)
  const [mx, setMx] = useState(emptyMatrix);    // the matrix's column-field count and totals (7e-3)
  const [filters, setFilters] = useState([]);   // builder filters (step 8)
  useEffect(() => {
    setDraft(emptyDraft());
    setNotice(null);
    setChosenType(null);
    setSettings(emptySettings());
    setFormat(emptyFormat());
    setOrder([]);
    setWidths({});
    setMx(emptyMatrix());
    setFilters([]);
    if (!selected) { setFieldState({ status: 'idle', fields: [], error: null }); return undefined; }
    const ctrl = new AbortController();
    setFieldState({ status: 'loading', fields: [], error: null });
    getFields(selected.id, { signal: ctrl.signal })
      .then((body) => {
        const fields = fieldsFromResponse(body);      // the fields, then the saved measures (13c-3)
        setFieldState({ status: 'ready', fields, error: null });
        const chart = pendingRestore.current;
        if (chart && chart.dataset_id === selected.id) {
          pendingRestore.current = null;
          const byKey = Object.fromEntries(fields.map((f) => [f.key, f]));
          const restored = fromSavedChart(chart, byKey, (d) => describeDraft(d, byKey));
          setDraft(restored.draft); setChosenType(restored.chosenType); setSettings(restored.settings);
          setFormat(restored.format); setOrder(restored.order); setWidths(restored.widths);
          setMx(restored.mx); setFilters(restored.filters);
          awaitBaseline.current = true;            // the next render's chart is the saved one
        }
      })
      .catch((error) => { if (!ctrl.signal.aborted) setFieldState({ status: 'error', fields: [], error }); });
    return () => ctrl.abort();
  }, [selected && selected.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const fieldsByKey = useMemo(() => Object.fromEntries(fieldState.fields.map((f) => [f.key, f])), [fieldState.fields]);
  const sentFilters = useMemo(() => specFilters(filters, fieldsByKey), [filters, fieldsByKey]);
  const fieldSpec = useMemo(() => withFilters(toSpec(selected && selected.id, draft), sentFilters), [selected, draft, sentFilters]);
  const reasons = useMemo(() => availability(draft), [draft]);
  const previewType = resolveType(chosenType, draft, fieldsByKey);
  const isTable = previewType === 'table';
  const isMatrix = previewType === 'matrix';
  const layout = useMemo(() => (isMatrix ? matrixLayout(draft, mx) : null), [isMatrix, draft, mx]);
  const spec = useMemo(
    () => (layout ? matrixSpec(fieldSpec, settings, layout) : applySettings(fieldSpec, settings)),
    [fieldSpec, settings, layout],
  );
  // The table's chips in display order; the order list is reconciled against the draft every render.
  const chips = useMemo(() => orderedChips(draft, order, fieldsByKey), [draft, order, fieldsByKey]);
  const columnOrder = useMemo(() => chips.map((c) => c.key), [chips]);
  const presentation = useMemo(
    () => toPresentation(format, previewType,
      isTable ? columnsPresentation(draft, columnOrder, widths) : isMatrix ? columnsPresentation(draft, [], widths) : null),
    [format, previewType, isTable, isMatrix, draft, columnOrder, widths],
  );
  const tableSort = isTable ? effectiveSort(fieldSpec, settings) : null;
  // "Saved" is true until something changes.
  const specKey = JSON.stringify([spec, presentation, format.title, previewType]);
  useEffect(() => {
    if (awaitBaseline.current) { awaitBaseline.current = false; setBaseline(specKey); return; }
    setSaving((st) => (st.status === 'saved' ? { status: 'idle', error: null } : st));
  }, [specKey]);
  const editingNow = Boolean(saved.chart && selected && saved.chart.dataset_id === selected.id);
  const unsaved = editingNow
    ? baseline !== null && specKey !== baseline
    : draft.dimensions.length > 0 || draft.measures.length > 0;
  useEffect(() => {
    if (!unsaved || !canSave) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };   // the browser shows its own "Leave site?" text
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved, canSave]);

  function onAddField(field) {
    if (isTable) { onColumnDrop({ field: field.key }, null); return; }
    if (isMatrix) { applyMatrix(addToMatrix(draft, mx, field)); return; }
    const result = addField(draft, field);
    setDraft(result.draft);
    setNotice(result.notice);
  }

  // --- the Matrix type's slots (7e-3) ----------------------------------------
  function applyMatrix(result) {
    setDraft(result.draft);
    setMx(result.mx);
    setNotice(result.notice);
  }

  function onMatrixDrop(key, well) {
    const field = fieldsByKey[key];
    if (field) applyMatrix(dropOnMatrix(draft, mx, field, well));   // only this dataset's own fields
  }

  function onMatrixMove(kind, index, well) {
    const item = kind === 'dimension' ? draft.dimensions[index] : draft.measures[index];
    if (item) applyMatrix(moveMatrixChip(draft, mx, kind, index, fieldsByKey[item.field], well));
  }

  function onMatrixRemove(kind, index) {
    applyMatrix({ ...removeMatrixItem(draft, mx, kind, index), notice: null });
  }

  // --- filters (step 8) ---------------------------------------------------------
  function onAddFilter(key) {
    const field = fieldsByKey[key];
    if (!field) return;                        // only this dataset's own fields
    const result = addFilter(filters, field);
    setFilters(result.filters);
    setNotice(result.notice);
  }

  function onChangeFilter(id, next) {
    setFilters((list) => list.map((f) => (f.id === id ? { ...next, id } : f)));
  }

  function onRemoveFilter(id) {
    setFilters((list) => list.filter((f) => f.id !== id));
  }

  // --- the Table type's Columns slot (7d-2) ---------------------------------
  // The draft's lists follow the display order, so the first grouping column
  // is the X axis if the user switches to a chart.
  function arrange(nextDraft, nextOrder) {
    setOrder(nextOrder);
    setDraft(alignDraft(nextDraft, nextOrder));
  }

  function onColumnDrop(what, beforeKey) {
    if (what.column) { arrange(draft, moveKey(columnOrder, what.column, beforeKey)); return; }
    const field = fieldsByKey[what.field];
    if (!field) return;                        // only this dataset's own fields can be dropped
    const result = addTableColumn(draft, field);
    setNotice(result.notice);
    if (result.key) arrange(result.draft, insertKey(columnOrder, result.key, beforeKey));
  }

  function onNudge(key, delta) {
    const i = columnOrder.indexOf(key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= columnOrder.length) return;
    arrange(draft, moveKey(columnOrder, key, delta < 0 ? columnOrder[j] : columnOrder[j + 1] || null));
  }

  function onColumnUse(key, use) {
    const chip = chips.find((c) => c.key === key);
    const result = setColumnUse(draft, chip, chip && fieldsByKey[chip.item.field], use);
    setNotice(result.notice);
    if (result.draft !== draft) arrange(result.draft, replaceKey(columnOrder, key, result.key));
  }

  function onColumnGrain(key, grain) {
    const chip = chips.find((c) => c.key === key);
    if (chip && chip.kind === 'dimension') setDraft((d) => setGrain(d, chip.index, grain));
  }

  function onColumnRemove(key) {
    const chip = chips.find((c) => c.key === key);
    if (!chip) return;
    setDraft((d) => removeItem(d, chip.kind, chip.index));
    setNotice(null);
  }

  function onTableSort(target) {
    setSettings((s) => ({ ...s, sort: toggleSort(fieldSpec, s, target) }));
  }

  function onTableResize(target, width) {
    const key = targetKey(target);
    if (!key) return;
    setWidths((w) => {
      if (width == null) { const { [key]: gone, ...rest } = w; return rest; }   // eslint-disable-line no-unused-vars
      return { ...w, [key]: width };
    });
  }

  function onRemove(kind, index) {
    setDraft((d) => removeItem(d, kind, index));
    setNotice(null);
  }

  function onDrop(key, well) {
    const field = fieldsByKey[key];
    if (!field) return;                        // only this dataset's own fields can be dropped
    const result = dropField(draft, field, well);
    setDraft(result.draft);
    setNotice(result.notice);
  }

  function onMoveChip(kind, index, well) {
    const item = kind === 'dimension' ? draft.dimensions[index] : draft.measures[index];
    if (!item) return;
    const result = moveChip(draft, kind, index, fieldsByKey[item.field], well);
    setDraft(result.draft);
    setNotice(result.notice);
  }

  function onAggregation(index, aggregation) {
    const result = setAggregation(draft, index, aggregation);
    setDraft(result.draft);
    setNotice(result.notice);
  }

  function onGrain(index, grain) {
    setDraft((d) => setGrain(d, index, grain));
  }

  function choose(id) {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('dataset', String(id)); else next.delete('dataset');
    // A different dataset is a new chart, never an edit of the one that was open.
    if (saved.chart && id !== saved.chart.dataset_id) next.delete('chart');
    setSearchParams(next, { replace: true });
  }

  async function save() {
    if (!selected || !spec || saving.status === 'saving') return;
    const editing = saved.chart && saved.chart.dataset_id === selected.id ? saved.chart : null;
    const payload = toSavePayload({ dataset: selected, spec, type: previewType, title: chartTitle(format, autoTitle), presentation });
    const key = specKey;                       // what is being saved, even if the user keeps editing meanwhile
    setSaving({ status: 'saving', error: null });
    try {
      const chart = editing ? await updateChart(editing.id, payload) : await createChart(payload);
      setSaved({ status: 'ready', chart, error: null });
      setBaseline(key);
      setSaving({ status: 'saved', error: null });
      if (!editing) {
        const next = new URLSearchParams(searchParams);
        next.set('chart', String(chart.id));
        setSearchParams(next, { replace: true });
      }
    } catch (error) {
      setSaving({ status: 'error', error });
    }
  }

  async function saveAsCopy() {
    if (!selected || !spec || saving.status === 'saving') return;
    const original = saved.chart && saved.chart.dataset_id === selected.id ? saved.chart : null;
    if (!original) return;
    const title = copyTitle(chartTitle(format, autoTitle), original.title);
    const payload = toSavePayload({ dataset: selected, spec, type: previewType, title, presentation, folderId: original.folder_id });
    const key = specKey;
    setSaving({ status: 'saving', error: null });
    try {
      const chart = await createChart(payload);
      setSaved({ status: 'ready', chart, error: null });
      setSaving({ status: 'saved', error: null });
      if (title !== chartTitle(format, autoTitle)) {
        setFormat((f) => ({ ...f, title }));      // show the copy's title; that render becomes the saved state
        awaitBaseline.current = true;
      } else {
        setBaseline(key);
      }
      const next = new URLSearchParams(searchParams);
      next.set('chart', String(chart.id));
      setSearchParams(next, { replace: true });
    } catch (error) {
      setSaving({ status: 'error', error });
    }
  }

  if (state.status === 'loading') {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--ui-text-muted)]" />
      </div>
    );
  }

  const hasFields = draft.dimensions.length > 0 || draft.measures.length > 0;
  const usedKeys = [...draft.dimensions.map((d) => d.field), ...draft.measures.map((m) => m.field).filter(Boolean)];
  const reset = () => {
    setDraft(emptyDraft()); setNotice(null); setChosenType(null); setSettings(emptySettings()); setFormat(emptyFormat());
    setOrder([]); setWidths({}); setMx(emptyMatrix()); setFilters([]);
    // A reset starts a new chart: the one that was open is left as it was saved.
    if (saved.chart) {
      const next = new URLSearchParams(searchParams);
      next.delete('chart');
      setSearchParams(next, { replace: true });
      setSaved({ status: 'idle', chart: null, error: null });
      setSaving({ status: 'idle', error: null });
      setBaseline(null);
    }
  };
  const appliedFilters = filters.filter((f) => !filterProblem(f, fieldsByKey[f.field])).map((f) => filterSummary(f, fieldsByKey[f.field]));
  const autoTitle = selected ? (hasFields ? describeDraft(draft, fieldsByKey) : selected.display_name) : 'Preview';
  const editing = saved.chart && selected && saved.chart.dataset_id === selected.id ? saved.chart : null;
  const legacy = saved.status === 'ready' && saved.chart && !isBuilderChart(saved.chart);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-[26px] font-semibold tracking-[-0.02em] text-[var(--ui-text)]">Chart builder</h1>
        <p className="mt-1 text-[13px] text-[var(--ui-text-muted)]">Build a chart from your synced data.</p>
      </div>

      {state.status === 'error' && (
        <Panel>
          <EmptyState icon={Database} title="Datasets couldn't be loaded"
            description={state.error && state.error.message}
            actionLabel="Try again" onAction={() => setAttempt((n) => n + 1)} />
        </Panel>
      )}

      {state.status === 'ready' && state.datasets.length === 0 && (
        <Panel>
          <EmptyState icon={Database} title="No datasets yet"
            description="Datasets appear here after a connector syncs. Connect a data source to get started."
            actionLabel="Go to connectors" onAction={() => navigate('/connectors')} />
        </Panel>
      )}

      {state.status === 'ready' && state.datasets.length > 0 && (
        <div className={`grid grid-cols-1 items-start gap-3 ${gridClass(panes)}`}>
          {/* Data: dataset and its fields */}
          {!panes.data ? <Rail icon={Table2} label="Data" onOpen={() => togglePane('data')} /> : (
          <Panel>
            <PanelHeader title="Data" side="left" onCollapse={() => togglePane('data')} />
            <PanelLabel id="builder-dataset-label">Dataset</PanelLabel>
            <Select value={selected ? String(selected.id) : ''} onValueChange={(v) => choose(Number(v) || null)}>
              <SelectTrigger aria-labelledby="builder-dataset-label" aria-label="Dataset">
                <SelectValue placeholder="Choose a dataset" />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectGroup key={g.key}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.items.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.display_name}</SelectItem>)}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <PanelHint>Only your organization's data is shown.</PanelHint>

            {selected && (
              <>
                <PanelLabel>Fields</PanelLabel>
                {fieldState.status === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-[var(--ui-text-muted)]" />}
                {fieldState.status === 'error' && <PanelHint className="text-red-400">{fieldState.error.message}</PanelHint>}
                {fieldState.status === 'ready' && <FieldList fields={fieldState.fields} onAdd={onAddField} usedKeys={usedKeys} />}
                {fieldState.status === 'ready' && (
                  <FiltersPanel filters={filters} fields={fieldState.fields} fieldsByKey={fieldsByKey}
                    onAdd={onAddFilter} onChange={onChangeFilter} onRemove={onRemoveFilter} />
                )}
              </>
            )}
          </Panel>
          )}

          {/* The chart: fills the window's height on wide screens and stays put while the side panels scroll */}
          <Panel className="flex flex-col p-6 lg:sticky lg:top-6 lg:h-[calc(100vh-13.5rem)] lg:min-h-[520px]">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="truncate text-[19px] font-semibold tracking-[-0.02em] text-[var(--ui-text)]">
                  {selected ? chartTitle(format, autoTitle) : autoTitle}
                </h2>
                <p className="mt-1 truncate text-[12.5px] text-[var(--ui-text-muted)]" title={appliedFilters.join(' · ') || undefined}>
                  {selected ? [`${sourceLabel(selected.source_key)} · ${selected.display_name}`, ...appliedFilters].join(' · ') : 'Choose a dataset to start.'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {(hasFields || format.title || presentation || filters.length > 0) && (
                  <Button variant="ghost" size="sm" onClick={reset}
                    className="rounded-full bg-[var(--ui-inset)] text-[var(--ui-text)] hover:bg-[var(--ui-inset-hover)]">
                    Reset
                  </Button>
                )}
                {editing && unsaved && canSave && (
                  <span role="status" className="text-xs text-[var(--ui-text-muted)]">Unsaved changes</span>
                )}
                {editing && canSave && (
                  <Button variant="ghost" size="sm" onClick={saveAsCopy} disabled={saving.status === 'saving'}
                    title="Save this as a new private chart; the original stays as it is"
                    className="rounded-full bg-[var(--ui-inset)] text-[var(--ui-text)] hover:bg-[var(--ui-inset-hover)]">
                    Save as copy
                  </Button>
                )}
                {selected && canSave && (
                  <Button size="sm" onClick={save} disabled={saving.status === 'saving' || saved.status === 'loading'}
                    className="rounded-full">
                    {saving.status === 'saving' ? 'Saving…' : editing ? 'Save changes' : 'Save'}
                  </Button>
                )}
              </div>
            </div>
            {saving.status === 'saved' && saved.chart && (
              <p role="status" className="mt-3 text-xs text-[var(--ui-text-muted)]">
                Saved. <Link to={`/charts/${saved.chart.id}`} className="underline hover:text-[var(--ui-text)]">Open the chart</Link>
              </p>
            )}
            {saving.status === 'error' && (
              <p role="alert" className="mt-3 text-xs text-red-400">{saveFailureMessage(saving.error)}</p>
            )}
            {saved.status === 'error' && (
              <p role="alert" className="mt-3 text-xs text-red-400">{saveFailureMessage(saved.error)}</p>
            )}
            {legacy && (
              <p role="status" className="mt-3 text-xs text-[var(--ui-text-muted)]">
                This chart was made with the previous builder.{' '}
                <Link to={`/charts/${saved.chart.id}`} className="underline hover:text-[var(--ui-text)]">Open its page</Link>
                {' '}to see what updating it would do, then update it there and it opens here.
              </p>
            )}
            {notice && <p role="status" className="mt-3 text-xs text-[var(--ui-text-muted)]">{notice}</p>}
            <div ref={chartBox} className="mt-4 min-h-[380px] flex-1">
              <ChartView spec={spec} type={previewType} height={previewHeight} presentation={presentation}
                tableSort={tableSort} onTableSort={isTable ? onTableSort : null}
                onTableResize={isTable || isMatrix ? onTableResize : null} />
            </div>
          </Panel>

          {/* Visual: type, what is in it, how it is ordered, how it reads */}
          {!panes.visual ? <Rail icon={BarChart3} label="Visual" onOpen={() => togglePane('visual')} /> : (
          <Panel>
            <PanelHeader title="Visual" side="right" onCollapse={() => togglePane('visual')} />
            {selected ? (
              <>
                <ChartTypeSwitcher value={previewType} reasons={reasons} onChange={setChosenType} />
                <PanelDivider />
                {isMatrix ? (
                  <MatrixWells draft={draft} mx={mx} fieldsByKey={fieldsByKey} onRemove={onMatrixRemove}
                    onDrop={onMatrixDrop} onMoveChip={onMatrixMove} onAggregation={onAggregation} onGrain={onGrain}
                    onTotals={(on) => setMx((m) => ({ ...m, totals: on }))} />
                ) : isTable ? (
                  <ColumnsWell chips={chips} fieldsByKey={fieldsByKey} showCount={draft.measures.length === 0}
                    onDrop={onColumnDrop} onUse={onColumnUse} onGrain={onColumnGrain} onRemove={onColumnRemove} onNudge={onNudge} />
                ) : (
                  <Wells draft={draft} fieldsByKey={fieldsByKey} onRemove={onRemove}
                    onDrop={onDrop} onMoveChip={onMoveChip} onAggregation={onAggregation} onGrain={onGrain} />
                )}
                <PanelDivider />
                <DataSettingsPanel spec={fieldSpec} settings={settings} fieldsByKey={fieldsByKey} onChange={setSettings} type={previewType}
                  matrixRows={layout ? layout.rows : 0} />
                <PanelDivider />
                <FormatPanel format={format} autoTitle={autoTitle} type={previewType} onChange={setFormat}
                  measures={draft.measures} fieldsByKey={fieldsByKey} />
              </>
            ) : (
              <PanelHint className="mt-0">Choose a dataset to pick a chart type and fields.</PanelHint>
            )}
          </Panel>
          )}
        </div>
      )}
    </div>
  );
}
